import { createSelector } from "@reduxjs/toolkit";

import { RootState } from "@/data/store";
import { getCurrentDate } from "@/utils/datetime";

import { ConnectionStatus } from "./types";

/** Select the `authority` slice state. */
export const selectAuthoritySlice = (state: RootState) => state.authority;

/** Select current set saving mechanism for classic Cadmus. */
export const selectSavingMechanism = (state: RootState) => {
  return state.authority.savingMechanism;
};

/** Select if saves are in flight. */
export const selectIsSavingInFlight = (state: RootState) => {
  return state.authority.savesInFlight.length > 0;
};

/** Select the loaded work ID. */
export const selectWorkId = (state: RootState) => state.authority.workId;

/** Select whether the current work is group work. */
export const selectIsGroupWork = (state: RootState) =>
  state.authority.isGroupWork;

/** Select if the work session should be locked. */
export const selectSessionLock = (state: RootState) =>
  state.authority.sessionLock;

/** Select if the network connection is intact. */
export const selectIsConnected = createSelector(
  selectAuthoritySlice,
  (authority) => authority.connectionStatus == ConnectionStatus.CONNECTED
);

/** Select if the Work is unable to accept further changes. */
export const selectIsDocumentError = createSelector(
  selectAuthoritySlice,
  (authority) => authority.documentError
);

/** Select if the work has an existing final submission. */
export const selectHasFinal = createSelector(
  selectAuthoritySlice,
  (authority) => authority.hasFinal
);

/** Select if the work has an existing draft submission. */
export const selectHasDraft = createSelector(
  selectAuthoritySlice,
  (authority) => authority.hasDraft
);

/** Select if the saving process has an error. */
export const selectSaveError = createSelector(
  selectAuthoritySlice,
  (authority) => authority.saveError
);

/** Select if the current save error is retryable. */
export const selectHasRetryableSaveError = createSelector(
  selectSaveError,
  selectIsConnected,
  (saveError, isConnected) =>
    !isConnected || (!!saveError && saveError.kind === "network")
);

/** Select if the submission preview page should be open. */
export const selectShowSubmitPreview = createSelector(
  selectAuthoritySlice,
  (authority) => authority.submitPreview
);

/** Select if the submission prompt modal should be shown. */
export const selectShowSubmitPrompt = createSelector(
  selectAuthoritySlice,
  (authority) => authority.submitPromptOpened !== null
);

export const selectSubmitDate = createSelector(
  selectAuthoritySlice,
  (authority) => {
    if (authority.submitPromptOpened !== null) {
      return authority.submitPromptOpened;
    }
    return getCurrentDate().toISOString();
  }
);

export const selectHasAcceptedSubmissionDeclarations = createSelector(
  selectAuthoritySlice,
  ({ hasAcceptedSubmissionDeclarations }) => hasAcceptedSubmissionDeclarations
);

/** Select if a submission request is in-flight. */
export const selectIsSubmitting = createSelector(
  selectAuthoritySlice,
  (authority) => authority.submitLoading
);

/** Select if the submission attempts are failing with an error. */
export const selectSubmitError = createSelector(
  selectAuthoritySlice,
  (authority) => authority.submitError
);

/** Select submission error if it is a network error. */
export const selectSubmitNetworkError = createSelector(
  selectSubmitError,
  (error) => (error?.kind === "network" ? error : null)
);

/** Select record of all latest local snapshots for answers. */
export const selectSnapshots = createSelector(
  selectAuthoritySlice,
  (answer) => answer.snapshots
);

/** Arg selector to pass the Answer Block ID to children selectors. */
export const selectAnswerBlockId = (_state: RootState, answerBlockId: string) =>
  answerBlockId;

/**
 * Select latest snapshot for an Answer.
 */
export const selectAnswerSnapshot = createSelector(
  selectSnapshots,
  selectAnswerBlockId,
  (snapshots, answerBlockId) => snapshots[answerBlockId]
);

/**
 * Select the next Work submit attempt number.
 */
export const selectSubmitAttemptCount = createSelector(
  selectAuthoritySlice,
  (authority) => authority.submitAttemptCount
);
